import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import { getAllCompanies } from "../../store/slices/superAdminCompanySlice";
import { Search, Filter, ChevronDown, Plus } from "lucide-react";
import Button from "../../components/UI/Button";
import { Input } from "../../components/UI/FormField";
import { Pagination } from "../../components/ListView/Pagination";
import CompanyTable from "../../components/superadmin/companies/CompanyTable";
import CompanyFilters from "../../components/superadmin/companies/CompanyFilters";
import { Link } from "react-router-dom";

interface CompanyFilters {
  search: string;
  status: string;
  page: number;
  limit: number;
}

const CompaniesListPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { companies, loading, pagination } = useAppSelector(
    (state: any) => state.superAdminCompanies,
  );

  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<CompanyFilters>({
    search: "",
    status: "",
    page: 1,
    limit: 20,
  });

  const prevSearch = useRef(filters.search);

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = {
      search: filters.search,
      status: filters.status,
      page: filters.page,
      limit: filters.limit,
    };

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getAllCompanies(params));
      } else {
        const t = setTimeout(() => dispatch(getAllCompanies(params)), 750);
        return () => clearTimeout(t);
      }
      return;
    }

    dispatch(getAllCompanies(params));
  }, [dispatch, filters]);

  const handleFiltersChange = (newFilters: Partial<CompanyFilters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
  };

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page });
  };

  const paginationDetails = {
    page: pagination.page,
    pages: pagination.pages,
    total: pagination.total,
    hasNext: pagination.page < pagination.pages,
    hasPrev: pagination.page > 1,
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-4">Companies</h1>
        <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
          {/* Search Bar */}
          <div className="flex-1 min-w-[250px] max-w-[250px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search companies..."
                value={filters.search}
                onChange={(e) =>
                  handleFiltersChange({ search: e.target.value })
                }
                className="pl-10 bg-white"
              />
            </div>
          </div>

          {/* Filter and Add Button */}
          <div className="flex items-center space-x-3">
            <div className="relative">
              <Button
                variant="outline"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="w-4 h-4" />
                <span>Filter</span>
                <ChevronDown className="h-4 w-4" />
              </Button>

              {showFilters && (
                <div className="absolute right-0 max-sm:left-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-10">
                  <CompanyFilters
                    filters={filters}
                    setShowFilters={setShowFilters}
                    onFiltersChange={handleFiltersChange}
                    onClearFilters={() =>
                      setFilters({
                        search: "",
                        status: "",
                        page: 1,
                        limit: 20,
                      })
                    }
                  />
                </div>
              )}
            </div>

            <Link to="/companies/create">
              <Button variant="primary">
                <Plus className="w-4 h-4 mr-2" />
                New Company
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <CompanyTable companies={companies} loading={loading} />

        {/* Pagination */}
        <Pagination
          pagination={paginationDetails}
          onPageChange={handlePageChange}
          pageSize={filters.limit}
        />
      </div>
    </div>
  );
};

export default CompaniesListPage;
